import {
  type BundleConfig,
  type BundleConfigOption,
  type DynamicGalleryCollection,
  type DynamicGalleryItem,
  type LastSelectedElement,
} from "@jti/fe-common";

import { type MultipacksQuantity } from "@product-details/common.types";
import { getQtyForMultipack } from "@product-details/utils";

const fallbackSticksImages = (placeholderImage: string, sticksCount: number) => {
  if (sticksCount <= 0) {
    return [];
  }

  const stickImages: string[] = new Array(sticksCount).fill(placeholderImage);

  return stickImages;
};

export const prepareInitialDynamicGallery = ({
  bundleConfig,
  placeholdersArray,
  multipacksQuantities,
  elementsDynamicGalleryUrls,
}: {
  bundleConfig: BundleConfig;
  placeholdersArray: DynamicGalleryItem[];
  multipacksQuantities: MultipacksQuantity[];
  elementsDynamicGalleryUrls: Record<string, string>;
}): DynamicGalleryCollection[] => {
  let initialGallery = [];
  initialGallery = placeholdersArray.map((item): DynamicGalleryCollection => {
    const singleElement = bundleConfig[item.itemNumber - 1];
    const optionId = singleElement?.optionId;
    const sticksCount = getQtyForMultipack(multipacksQuantities, optionId);

    if (item.itemNumber < 1) {
      throw new Error(`Item number must be greater than or equal to 1 for item: ${JSON.stringify(item)}`);
    }

    if (typeof optionId !== "number") {
      throw new Error(`OptionId is not a valid number for item: ${JSON.stringify(item)}`);
    }

    const defaultOption = singleElement.options.find((option: BundleConfigOption) => option.isDefault);

    if (singleElement.type === "multi") {
      const defaultOptionUrl = defaultOption?.id ? elementsDynamicGalleryUrls[defaultOption.id] : undefined;
      const stickImageUrl = defaultOptionUrl ?? item.placeholderImage;

      return {
        ...item,
        placeholderImage: fallbackSticksImages(stickImageUrl, sticksCount),
        optionId,
        sicksPlaceHolderImage: item.placeholderImage,
      };
    }

    if (defaultOption) {
      return {
        ...item,
        optionId,
        placeholderImage: elementsDynamicGalleryUrls[defaultOption.id] ?? item.placeholderImage,
      };
    }

    return {
      ...item,
      optionId,
    };
  });

  return initialGallery;
};

const handleSticksImages = (
  selectedElement: LastSelectedElement,
  sticksCount: number,
  elementsDynamicGalleryUrls: Record<string, string>,
  placeholderImage = ""
) => {
  const imageCollection: string[] = new Array(sticksCount).fill(placeholderImage);
  let selectedOptionsQuantity = 0;

  if (selectedElement.quantities && Array.isArray(selectedElement.quantities)) {
    selectedElement.quantities.forEach((item) => {
      const { selection_id, qty } = item;
      if (selection_id in elementsDynamicGalleryUrls) {
        const image = elementsDynamicGalleryUrls[selection_id];
        for (let i = 0; i < qty; i++) {
          imageCollection[sticksCount - 1 - selectedOptionsQuantity] = image;
          selectedOptionsQuantity++;
        }
      }
    });
  }

  return imageCollection;
};

export const retriveImageCollection = (
  selectedElement: LastSelectedElement,
  multipacksQuantities: MultipacksQuantity[],
  elementsDynamicGalleryUrls: Record<string, string>,
  imageCollection: DynamicGalleryCollection[] = []
) => {
  const selectedOptionIdString = selectedElement.optionId.toString();

  const updatedImageCollection = imageCollection.map((item) => {
    const optionId = item.optionId.toString();
    const sticksCount = getQtyForMultipack(multipacksQuantities, item.optionId);

    if (selectedOptionIdString === optionId) {
      if (selectedElement.type === "multi") {
        return {
          ...item,
          placeholderImage: handleSticksImages(
            selectedElement,
            sticksCount,
            elementsDynamicGalleryUrls,
            item.sicksPlaceHolderImage
          ),
        };
      } else if (selectedElement.productId && elementsDynamicGalleryUrls[selectedElement.productId]) {
        return {
          ...item,
          placeholderImage: elementsDynamicGalleryUrls[selectedElement.productId],
        };
      }
    }

    return item;
  });

  return updatedImageCollection;
};
